import React, { useState, useEffect } from 'react';
import { CalendarPicker, SecondaryButton, Text } from 'library';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import Grid from '@mui/material/Grid';
import { BasicDialog } from 'library';
import { useUserContext } from 'UserContext';
import { consoleLogInDev, track, client } from 'shared';
import { v4 as uuidv4 } from 'uuid';
import 'antd/dist/antd.css';
import useSignUpAction from 'parent-portal/login/SignUpAction';
import { Provider, ProviderJobType } from 'models';
import { useMediaQuery } from '@mui/material';
import theme from 'theme';
import SendToOthers from './SendToOthers';
import InstantAvailability, { IHardBooking } from './InstantAvailability';
import TimeAndRate from './TimeAndRate';
import { hasNoAvailibityForDate } from './Utils';
import moment from 'moment';

const BookFromCalendarModal = ({
    visible,
    setVisible,
    setShowSuccessDialog,
    dateValue,
    provider,
    availability,
    search,
    setDateValue,
    quickBook,
}: {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    setShowSuccessDialog: (showSuccessDialog: boolean) => void;
    dateValue: Date | null;
    setDateValue?: (dateValue: Date | null) => void;
    provider?: Provider;
    availability?: any;
    search?: string;
    quickBook?: boolean;
}) => {
    const { user } = useUserContext();
    const breakpoint = useMediaQuery(theme.breakpoints.down('sm'));
    const [bookedJob, setBookedJob] = useState<IHardBooking>();

    const [sendToOthers, setSendToOthers] = useState<boolean>(true);
    const [hourlyRate, setHourlyRate] = useState<number>(15);
    const [startTime, setStartTime] = useState<Date>();
    const [endTime, setEndTime] = useState<Date>();
    const [jobTypeInterests, setJobTypeInterests] = useState<Array<ProviderJobType>>([]);
    const [selectedJobTypeInterest, setSelectedJobTypeInterest] = useState<ProviderJobType | null>();
    const [temporaryFamilyId, setTemporaryFamilyId] = useState<string>();
    const [details, setDetails] = useState<string>('');
    const [headline, setHeadline] = useState<string>('');
    const { SignUpWrapper, signUp } = useSignUpAction();

    useEffect(() => {
        if (dateValue) {
            let st = moment(dateValue).add(8, 'hours');
            let ed = moment(st).add(3, 'hours');
            setStartTime(st.toDate());
            setEndTime(ed.toDate());
        }
    }, [dateValue]);

    useEffect(() => {
        if (selectedJobTypeInterest) {
            setHeadline(defaultHeadline());
            setDetails(defaultFamilyComments());
        }
    }, [selectedJobTypeInterest]);

    function formCompleted() {
        return !!(
            (bookedJob || !provider) &&
            dateValue &&
            startTime &&
            endTime &&
            selectedJobTypeInterest &&
            !!hourlyRate &&
            details &&
            headline
        );
    }

    function getPreferredSitters() {
        if (provider) {
            return { preferred_sitters: [{ sitter: provider.user.id }] };
        }
        return { preferred_sitters: [] };
    }

    function defaultFamilyComments() {
        let family_comments = `This family is looking for ${selectedJobTypeInterest?.job_type.name} services. Message this family to find out more.`;
        if (provider) {
            family_comments = `This family saw you were available and requested a direct booking for ${selectedJobTypeInterest?.job_type.name}. Message this family if you have any questions.`;
        }
        return family_comments;
    }

    function defaultHeadline() {
        let defaultHeadline = `${selectedJobTypeInterest?.job_type?.name || 'Booking Request'}`;
        if (user) {
            defaultHeadline = `${user.last_name} Family - ${
                selectedJobTypeInterest?.job_type?.name || 'Booking Request'
            }`;
        }
        return defaultHeadline;
    }

    function postJobRequest() {
        track(`${quickBook ? 'Quick Book' : 'Hard Availability'}: Clicked Post Job Request`, {
            providerId: provider?.id,
        });
        if (!formCompleted()) {
            return null;
        }

        client('api/ongoing/request-available-provider/', {
            method: 'POST',
            body: {
                family: user?.family?.[0]?.id,
                temporary_family_id: temporaryFamilyId,
                status: 'ACTIVE',
                preferred_sitters: getPreferredSitters(),
                job_type: selectedJobTypeInterest?.job_type?.id,
                request_type: selectedJobTypeInterest?.job_type?.name === 'Child Care' ? 'CHILD_CARE' : 'OTHER',
                headline: headline,
                family_comments: details,
                preferred_education_level: 'NO_PREFERENCE',
                times_of_day: '',
                start_date: startTime,
                end_date: endTime,
                pay: hourlyRate,
                long_term: false,
                quick_fill: provider ? true : false,
                ongoing: false,
                automatic: false,
                hard_availability: !quickBook,
                transportation: false,
                multiple_providers: false,
                requested: true,
                send_to_others: sendToOthers,
            },
        })
            .then((res) => {
                if (!temporaryFamilyId) {
                    setShowSuccessDialog(true);
                    setVisible(false);
                } else {
                    signUp(quickBook ? 'Quick Book' : 'Book From Calendar');
                }
            })
            .catch(consoleLogInDev);
    }

    useEffect(() => {
        if (!search) {
            return;
        }
        setSelectedJobTypeInterest(
            provider?.job_type_interest.find(
                (j) =>
                    j.job_type.name.toUpperCase().replace(/^\s+|\s+$/g, '') ===
                    search.toUpperCase().replace(/^\s+|\s+$/g, ''),
            ),
        );

        if (['Dog Walking', 'Pet Boarding'].includes(search)) {
            setSelectedJobTypeInterest(provider?.job_type_interest.find((j) => j.job_type.name === 'Pet Care'));
        }
    }, [search]);

    useEffect(() => {
        if (!user) {
            let tmpId = uuidv4();
            setTemporaryFamilyId(tmpId);
            localStorage.setItem('temporaryFamilyId', tmpId);
        }

        setJobTypeInterests(provider?.job_type_interest || []);
        if (!provider) {
            client('api/job-types/')
                .then((response) => {
                    if (!provider) {
                        response = response.map((jobType: any) => ({
                            job_type: { id: jobType.id, name: jobType.name },
                        }));
                    }
                    setJobTypeInterests(response);
                })
                .catch(consoleLogInDev);
        }
    }, [provider]);

    function handleClose() {
        setVisible(false);
        setSelectedJobTypeInterest(undefined);
        setBookedJob(undefined);
        setDateValue?.(null);
    }

    function titleText() {
        let base = provider ? `Book ${provider.user.first_name}` : `Job details`;
        return provider && selectedJobTypeInterest ? `${base} for ${selectedJobTypeInterest.job_type.name}` : base;
    }

    function handleBookingSelect(booking: IHardBooking) {
        setStartTime(booking.start_date);
        setEndTime(booking.end_date);
        setBookedJob(booking);
    }

    function selectJobTypeInterest(x: ProviderJobType) {
        setSelectedJobTypeInterest(x);
        setHourlyRate(x.hourly_rate ?? 15);
    }

    function JobTypeInterestSelection({
        jobTypeInterests,
        onClick,
    }: {
        jobTypeInterests: ProviderJobType[];
        onClick: (x: ProviderJobType) => void;
    }) {
        let highlighted = ['Child Care', 'Pet Care', 'House Cleaning'];
        let first = jobTypeInterests.filter((x) => highlighted.includes(x.job_type.name));
        let second = jobTypeInterests.filter((x) => !highlighted.includes(x.job_type.name));
        return (
            <Grid xs={12}>
                {[...first, ...second].map((x: ProviderJobType) => (
                    <SecondaryButton
                        buttonStyle={{ margin: 10, maxWidth: breakpoint ? '100%' : '40%' }}
                        onClick={() => onClick(x)}
                    >
                        {x.job_type.name}
                    </SecondaryButton>
                ))}
            </Grid>
        );
    }

    return (
        <BasicDialog
            isOpen={visible}
            onClose={handleClose}
            dialogTitle={titleText()}
            buttonText={
                formCompleted() && provider
                    ? `Book ${provider.user.first_name} for ${startTime?.toLocaleString('en-US', {
                          hour: 'numeric',
                          hour12: true,
                      })} - ${endTime?.toLocaleString('en-US', { hour: 'numeric', hour12: true })}`
                    : quickBook
                    ? 'Post'
                    : 'Book'
            }
            buttonEnabled={formCompleted()}
            onSubmit={postJobRequest}
            closeButton
        >
            <Grid container xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    {!selectedJobTypeInterest ? (
                        <>
                            <Text variant="body1">Select Job Type For Booking:</Text>
                            <JobTypeInterestSelection
                                onClick={selectJobTypeInterest}
                                jobTypeInterests={jobTypeInterests}
                            />
                        </>
                    ) : !bookedJob && availability && dateValue ? (
                        provider && (
                            <InstantAvailability
                                provider={provider}
                                bookedJob={bookedJob}
                                setBookedJob={handleBookingSelect}
                                availability={availability}
                                dateValue={dateValue}
                            />
                        )
                    ) : !dateValue && setDateValue ? (
                        <Grid item xs={12} md={8}>
                            <CalendarPicker
                                shouldDisableDate={(date) => {
                                    return hasNoAvailibityForDate(date as Date, availability);
                                }}
                                date={dateValue}
                                onChange={(newValue) => {
                                    setDateValue(newValue as Date);
                                }}
                            />
                        </Grid>
                    ) : (
                        <>
                            {dateValue && startTime && endTime ? (
                                <TimeAndRate
                                    headline={headline}
                                    details={details}
                                    setHeadline={setHeadline}
                                    setDetails={setDetails}
                                    startTime={startTime}
                                    endTime={endTime}
                                    setStartTime={setStartTime}
                                    setEndTime={setEndTime}
                                    hourlyRate={hourlyRate ?? 15}
                                    setHourlyRate={setHourlyRate}
                                    minRate={selectedJobTypeInterest.hourly_rate ?? 15}
                                    bookedJob={bookedJob}
                                    provider={provider}
                                    dateValue={dateValue}
                                />
                            ) : null}
                            {provider ? (
                                <Grid style={{ marginTop: 20 }}>
                                    <SendToOthers
                                        sendToOthers={sendToOthers}
                                        setSendToOthers={setSendToOthers}
                                        providerName={provider.user.first_name}
                                    />
                                </Grid>
                            ) : null}
                        </>
                    )}
                </LocalizationProvider>
                <SignUpWrapper />
            </Grid>
        </BasicDialog>
    );
};

export default BookFromCalendarModal;
